/**
 * i18n key literals for notification rows and their CTAs.
 *
 * Why a map of literal strings instead of building keys inline:
 * `src/lib/i18n/locales.test.ts` flags any English key that never appears as a
 * *quoted literal* anywhere in `src/**` — so a template literal like
 * ``t(`notifications.type.${type}.title`)`` would register every type key as
 * unused and fail the build. Spelling the keys out here satisfies that check
 * with no test-file edit, and `Record<NotificationType, …>` makes `tsc` refuse
 * a type that arrives without copy.
 *
 * Holding key *strings* at module scope is fine. The CLAUDE.md ban is on
 * calling `t()` at module scope, which this file does not do.
 */

import type { MeetingPanel, NotificationTarget } from "./target";
import type { NotificationType } from "./types";

export interface NotificationCopyKeys {
  title: string;
  body: string;
  /**
   * Body for a manager, whose alert covers their reports' deals as well as their own.
   *
   * Only the five deals alerts have one: their CTA opens a view scoped to the user AND their
   * direct reports (`isAlertDealListId` in DealsPage widens `scopedOwnerIds`), so "these
   * deals of yours" describes something narrower than what the click shows. Every other
   * type is genuinely about the reader alone, and `bodyFor` falls back to `body`.
   */
  bodyTeam?: string;
}

export const NOTIFICATION_KEYS: Record<NotificationType, NotificationCopyKeys> = {
  weekStart: {
    title: "notifications.type.weekStart.title",
    body: "notifications.type.weekStart.body",
  },
  weekdayMorning: {
    title: "notifications.type.weekdayMorning.title",
    body: "notifications.type.weekdayMorning.body",
  },
  weekdayEndOfDay: {
    title: "notifications.type.weekdayEndOfDay.title",
    body: "notifications.type.weekdayEndOfDay.body",
  },
  calendarSyncCompleted: {
    title: "notifications.type.calendarSyncCompleted.title",
    body: "notifications.type.calendarSyncCompleted.body",
  },
  firstMeeting: {
    title: "notifications.type.firstMeeting.title",
    body: "notifications.type.firstMeeting.body",
  },
  followUpDrafted: {
    title: "notifications.type.followUpDrafted.title",
    body: "notifications.type.followUpDrafted.body",
  },
  dealSuggestion: {
    title: "notifications.type.dealSuggestion.title",
    body: "notifications.type.dealSuggestion.body",
  },
  taskSuggestion: {
    title: "notifications.type.taskSuggestion.title",
    body: "notifications.type.taskSuggestion.body",
  },

  // Literal strings, not template literals: locales.test.ts only counts a key as used when
  // it appears quoted in source, so `notifications.type.${type}.title` would register every
  // one of these as unused and fail the build.
  assignedTask: {
    title: "notifications.type.assignedTask.title",
    body: "notifications.type.assignedTask.body",
  },
  assignedDealOwner: {
    title: "notifications.type.assignedDealOwner.title",
    body: "notifications.type.assignedDealOwner.body",
  },
  closeDateInPast: {
    title: "notifications.type.closeDateInPast.title",
    body: "notifications.type.closeDateInPast.body",
    bodyTeam: "notifications.type.closeDateInPast.bodyTeam",
  },
  noActivity: {
    title: "notifications.type.noActivity.title",
    body: "notifications.type.noActivity.body",
    bodyTeam: "notifications.type.noActivity.bodyTeam",
  },
  noNextSteps: {
    title: "notifications.type.noNextSteps.title",
    body: "notifications.type.noNextSteps.body",
    bodyTeam: "notifications.type.noNextSteps.bodyTeam",
  },
  fewContacts: {
    title: "notifications.type.fewContacts.title",
    body: "notifications.type.fewContacts.body",
    bodyTeam: "notifications.type.fewContacts.bodyTeam",
  },
  weeklyPipelineChanges: {
    title: "notifications.type.weeklyPipelineChanges.title",
    body: "notifications.type.weeklyPipelineChanges.body",
    bodyTeam: "notifications.type.weeklyPipelineChanges.bodyTeam",
  },
  dailyBriefing: {
    title: "notifications.type.dailyBriefing.title",
    body: "notifications.type.dailyBriefing.body",
  },
  actionItemsDue: {
    title: "notifications.type.actionItemsDue.title",
    body: "notifications.type.actionItemsDue.body",
  },
  crmSyncCompleted: {
    title: "notifications.type.crmSyncCompleted.title",
    body: "notifications.type.crmSyncCompleted.body",
  },
  contactJobTitleChanged: {
    title: "notifications.type.contactJobTitleChanged.title",
    body: "notifications.type.contactJobTitleChanged.body",
  },
  contactTitleChangeSuggested: {
    title: "notifications.type.contactTitleChangeSuggested.title",
    body: "notifications.type.contactTitleChangeSuggested.body",
  },
  contactCompanyChanged: {
    title: "notifications.type.contactCompanyChanged.title",
    body: "notifications.type.contactCompanyChanged.body",
  },
};

/**
 * The body a job-change question shows once it is resolved — by someone else, by the viewer,
 * or dismissed by the viewer. Keyed by the backend's `params.resolution` (an accept mode or
 * DISMISSED), never by any display text. Literal keys for the same locales.test.ts reason as
 * above. `self` is "You moved Jane…", `other` is "Afeez moved Jane…".
 */
export const RESOLUTION_KEYS: Record<string, { self: string; other: string }> = {
  CREATE_NEW: {
    self: "notifications.resolved.CREATE_NEW.self",
    other: "notifications.resolved.CREATE_NEW.other",
  },
  LINK_EXISTING: {
    self: "notifications.resolved.LINK_EXISTING.self",
    other: "notifications.resolved.LINK_EXISTING.other",
  },
  MOVE: {
    self: "notifications.resolved.MOVE.self",
    other: "notifications.resolved.MOVE.other",
  },
  TITLE_ONLY: {
    self: "notifications.resolved.TITLE_ONLY.self",
    other: "notifications.resolved.TITLE_ONLY.other",
  },
  DISMISSED: {
    self: "notifications.resolved.DISMISSED.self",
    other: "notifications.resolved.DISMISSED.self",
  },
};

/** Resolution copy for a row, or null when the row is not a resolved job-change question. */
export function resolutionKeyFor(
  params: Record<string, unknown>,
  viewerUserId: number | string | null | undefined,
): string | null {
  const resolution = typeof params.resolution === "string" ? params.resolution : null;
  const keys = resolution ? RESOLUTION_KEYS[resolution] : undefined;
  if (!keys) return null;
  const byViewer = viewerUserId != null && String(params.resolvedByUserId) === String(viewerUserId);
  return byViewer ? keys.self : keys.other;
}

/**
 * CTA label keyed by the target's *destination*, not by notification type.
 *
 * The button says what it does, which is a property of where it goes — so a new type
 * reusing an existing destination needs no new CTA string.
 *
 * Kind alone is not the destination, though, which is the mistake this used to make: a
 * meeting target also carries a PANEL, and the panels are genuinely different screens.
 * Every meeting-shaped type therefore said "Open meeting" — a drafted follow-up, a set of
 * suggested tasks and a first-meeting summary all got the same button, and none of them
 * told the user which tab they were about to land on. Panels get their own labels below.
 */
const CTA_KEYS: Record<NotificationTarget["kind"], string | null> = {
  today: "notifications.cta.viewToday",
  person: "notifications.cta.viewPerson",
  company: "notifications.cta.viewCompany",
  deal: "notifications.cta.viewDeal",
  task: "notifications.cta.openTask",
  deals: "notifications.cta.seeDeals",
  settings: "notifications.cta.openSettings",
  meeting: "notifications.cta.openMeeting",
  dealSuggestion: "notifications.cta.openDeal",
  composeEmail: "notifications.cta.openDraft",
  contactSuggestion: "notifications.cta.reviewChange",
  // No destination, so no button to label.
  none: null,
};

/**
 * Per-panel overrides for meeting targets.
 *
 * Only the panels a notification actually lands on need one; anything else falls back to
 * the generic "Open meeting", which is honest rather than wrong. Deliberately NOT an
 * exhaustive Record over MeetingPanel: adding a panel to that union should not force a
 * translation in ten languages for a destination nothing points at yet.
 */
const MEETING_PANEL_CTA_KEYS: Partial<Record<MeetingPanel, string>> = {
  follow_up: "notifications.cta.openEmail",
  tasks: "notifications.cta.openTasks",
  summary: "notifications.cta.openSummary",
};

/** The CTA label key for a target, or null when the row has no action. */
export function ctaKeyFor(target: NotificationTarget): string | null {
  if (target.kind === "meeting") {
    return MEETING_PANEL_CTA_KEYS[target.panel] ?? CTA_KEYS.meeting;
  }
  return CTA_KEYS[target.kind];
}

/**
 * The body key to render: the team variant when the reader manages someone, else the plain
 * one. Types without a team variant always get `body`.
 */
export function bodyKeyFor(keys: NotificationCopyKeys, hasDirectReports: boolean): string {
  return hasDirectReports && keys.bodyTeam ? keys.bodyTeam : keys.body;
}
